import React, { ChangeEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FormikProps } from 'formik';
import orderBy from 'lodash/orderBy';
import {
  Button,
  Checkbox,
  DatePicker,
  LayoutSidebar,
  Message,
  PageTitle,
  SectionHeader,
  Select,
  SidebarHeaderTitle,
  SidebarList,
  SidebarListItem,
  SidebarListItemLabel,
  SidebarListItemValue,
  SidebarTermsFooter,
  SidebarTitle,
  TooltipIcon,
  TooltipWrapper,
  ValidationMessage,
} from 'components';
import { ISelectOption } from 'components/Select/types';
import { appConfig } from 'config/app';
import { Appearance } from 'design/types';
import { selectAPIRequestErrors } from 'store/ApiClient';
import { Payment } from 'store/Payment';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, IPayee, PermissionType } from 'types';
import { centsToDollarsDisplay, hasSitePermission } from 'utils';
import {
  BusinessDateInputWrapper,
  DesktopDescription,
  MessageContainer,
  MobileDescription,
  MPContent,
  PaymentCategoryWrapper,
  PaymentDescription,
  PRSContent,
  PRSHeader,
  PRSHeaderContent,
  PRSHeaderSection,
  ReviewPaymentsList,
  SidebarActions,
  SidebarContentLayout,
  SidebarSection,
  SidebarSectionMobile,
} from './components';
import MakePaymentHeader from './MakePaymentHeader';
import { MakePaymentProps, MakePaymentSidebarProps } from './MakePaymentTypes';

interface IMakePaymentForm {
  payees: IPayee[];
  description: string;
  authorizedAgreement: boolean;
  businessDate: string;
  paymentCategory?: string;
}
interface IReviewPaymentProps extends MakePaymentProps, MakePaymentSidebarProps {
  createPaymentsEnabled: boolean;
  description?: string;
  totalTips: number;
  totalInstantTips: number;
  totalPayrollTips: number;
  tipPoolLimit?: number;
  payees: IPayee[];
  acceptAgreementProps: { value: boolean };
  paymentCategoryProps: { value: string };
  paymentCategories: string[];
  valid: boolean;
  setFieldValue: (field: string, value: unknown) => void;
  onSubmit: () => void;
  businessDate?: string;
  setFieldTouched: (touched: string) => void;
  businessDateError?: string;
  businessDateTouched?: boolean;
  currentUserName: string;
  isProcessing: boolean;
  isLoading?: boolean;
  siteUsesHoldbacks?: boolean;
  totalHoldbacks: number;
  showOverdraftWarning: boolean;
  overdraftText: React.ReactNode;
  overdraftContinue: boolean;
  formik: FormikProps<IMakePaymentForm>;
}

dayjs.extend(customParseFormat);

const PaymentReview = ({
  balance,
  createPaymentsEnabled,
  description,
  onBack,
  sidebarOpen,
  currentSite,
  totalTips,
  totalInstantTips,
  totalPayrollTips,
  payees,
  acceptAgreementProps,
  paymentCategoryProps,
  paymentCategories,
  valid,
  setFieldValue,
  onSubmit,
  businessDate,
  setFieldTouched,
  businessDateError,
  currentUserName,
  businessDateTouched,
  isProcessing,
  isLoading,
  totalHoldbacks,
  showOverdraftWarning,
  overdraftText,
  overdraftContinue,
  formik,
  siteUsesHoldbacks = false,
}: IReviewPaymentProps) => {
  const apiSubmitErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payment.CREATE_PAYMENT_REQUEST)) as IApiErrorPayloadWithCode;

  // UI State
  const hasSubmitPaymentPermissions = hasSitePermission(currentSite?.permissions, PermissionType.PAYMENT_SUBMIT);
  const submitButtonDisabled =
    !createPaymentsEnabled || !valid || isProcessing || isLoading || !overdraftContinue || !hasSubmitPaymentPermissions || acceptAgreementProps.value !== true || (paymentCategories?.length > 0 && !paymentCategoryProps.value);

  const convertDollars = (n: number) => (n / 100).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');

  // Data
  const totalPaid = siteUsesHoldbacks ? totalInstantTips - totalHoldbacks : totalInstantTips;
  const totalNet = siteUsesHoldbacks ? totalTips - totalHoldbacks : totalTips;

  const businessDay = (
    <>
      <SidebarListItem>
        <SidebarListItemLabel>Business Day</SidebarListItemLabel>
        <BusinessDateInputWrapper>
          <DatePicker
            width={132}
            {...(businessDate
              ? {
                  defaultValue: businessDate,
                }
              : {})}
            onBlur={() => setFieldTouched('businessDate')}
            // disabled={isLoading}
            data-cy="payment-business-date-picker"
            onChange={async (dateString: string) => {
              await setFieldValue('businessDate', dateString);
              setFieldTouched('businessDate');
            }}
          />
          {businessDateTouched && !!businessDateError && (
            <ValidationMessage data-cy="business-date-error" hasError>
              {businessDateError || ''}
            </ValidationMessage>
          )}
        </BusinessDateInputWrapper>
      </SidebarListItem>
    </>
  );

  const sentBy = (
    <SidebarListItem>
      <SidebarListItemLabel>Sent By</SidebarListItemLabel>
      <SidebarListItemValue>{currentUserName}</SidebarListItemValue>
    </SidebarListItem>
  );

  const paymentCategoryOptions = useMemo(
    () =>
      orderBy(
        paymentCategories?.map((category) => ({
          value: category,
          label: category,
        })),
        [(cat) => cat.label.toLowerCase()],
        ['asc']
      ),
    [paymentCategories]
  );

  const paymentCategorySelector = paymentCategories?.length > 0 && (
    <SidebarListItem>
      <SidebarListItemLabel>Category</SidebarListItemLabel>
      <PaymentCategoryWrapper>
        <Select
          data-cy="payment-category-selector"
          values={[paymentCategoryOptions.find((opt) => opt.value === paymentCategoryProps.value)]}
          options={paymentCategoryOptions}
          onChange={(selected: ISelectOption<string>[]) => {
            setFieldValue('paymentCategory', selected[0]?.value);
          }}
          placeholder="Please select"
          error={acceptAgreementProps.value && !paymentCategoryProps.value}
        />
      </PaymentCategoryWrapper>
    </SidebarListItem>
  );

  return (
    <LayoutSidebar
      data-cy="payment-review-sidebar"
      fixedMobile={false}
      content={
        <>
          <MakePaymentHeader pageHeaderContent={<PageTitle data-cy="payment-review-site-balance" isLoading={isLoading} title={currentSite.name} subtitle={`Account Balance: ${centsToDollarsDisplay(balance)}`} />} />

          <MPContent>
            <SectionHeader isLoading={isLoading} title="Review Payments" value={`(${payees.length})`} />
            <ReviewPaymentsList isLoading={isLoading} payees={payees} siteUsesHoldback={siteUsesHoldbacks} />
          </MPContent>
        </>
      }
      sidebar={
        <>
          <PRSHeader>
            <PRSHeaderSection>
              <SidebarHeaderTitle>
                <SidebarTitle data-cy="title-total" title={`Pay Now: ${centsToDollarsDisplay(totalPaid)}`} />
              </SidebarHeaderTitle>
            </PRSHeaderSection>

            <PRSHeaderContent data-cy="sent-by">
              {businessDay}
              {sentBy}
              {paymentCategorySelector}
            </PRSHeaderContent>
          </PRSHeader>

          <PRSContent>
            <SidebarContentLayout>
              <SidebarSectionMobile>
                <SidebarList>
                  {businessDay}
                  {sentBy}
                  {paymentCategorySelector}
                </SidebarList>
              </SidebarSectionMobile>
              <MobileDescription>
                <PaymentDescription data-cy="mobile-payment-description" description={description} />
              </MobileDescription>
              <DesktopDescription>
                <PaymentDescription data-cy="desktop-payment-description" description={description} />
              </DesktopDescription>

              <SidebarSection>
                <SidebarList>
                  <SidebarListItem>
                    <SidebarListItemLabel size="small">
                      <strong>Amount Entered (Gross)</strong>
                    </SidebarListItemLabel>
                    <SidebarListItemValue size="small" data-cy="total-paid">
                      <strong>{totalTips > 0 ? centsToDollarsDisplay(totalTips) : '$0.00'}</strong>
                    </SidebarListItemValue>
                  </SidebarListItem>
                  {siteUsesHoldbacks && (
                    <>
                      <SidebarListItem>
                        <SidebarListItemLabel size="small" color="bluegray">
                          <TooltipWrapper>
                            Holdbacks
                            <TooltipIcon id="holdback">Amount withheld to cover expenses</TooltipIcon>
                          </TooltipWrapper>
                        </SidebarListItemLabel>
                        <SidebarListItemValue data-cy="total-holdbacks" size="small" color="bluegray">
                          -$
                          {convertDollars(totalHoldbacks)}
                        </SidebarListItemValue>
                      </SidebarListItem>
                    </>
                  )}
                  <SidebarListItem>
                    <SidebarListItemLabel size="small" color="bluegray">
                      Net
                    </SidebarListItemLabel>
                    <SidebarListItemValue size="small" color="bluegray">
                      {centsToDollarsDisplay(totalNet)}
                    </SidebarListItemValue>
                  </SidebarListItem>
                  <SidebarListItem>
                    <SidebarListItemLabel size="small" color="bluegray">
                      Payroll
                    </SidebarListItemLabel>
                    <SidebarListItemValue size="small" color="bluegray">
                      {centsToDollarsDisplay(totalPayrollTips)}
                    </SidebarListItemValue>
                  </SidebarListItem>
                  <SidebarListItem>
                    <SidebarListItemLabel size="small" color="bluegray">
                      Instant
                    </SidebarListItemLabel>
                    <SidebarListItemValue size="small" color="bluegray">
                      {centsToDollarsDisplay(totalPaid)}
                    </SidebarListItemValue>
                  </SidebarListItem>
                  <SidebarListItem>
                    <SidebarListItemLabel size="large">Pay Now</SidebarListItemLabel>
                    <SidebarListItemValue size="large" data-cy="total-paid">
                      <strong>{totalPaid > 0 ? centsToDollarsDisplay(totalPaid) : '$0.00'}</strong>
                    </SidebarListItemValue>
                  </SidebarListItem>
                </SidebarList>
              </SidebarSection>

              {!createPaymentsEnabled && (
                <MessageContainer>
                  <Message dataCy="payments-disabled" key="site-payments-disabled" title="Payments Disabled" message="Payments are disabled for this site. You can not submit payments at this time." appearance={Appearance.WARNING} />
                </MessageContainer>
              )}
              {!isLoading && typeof formik.errors?.payees === 'string' && (
                <MessageContainer>
                  <Message dataCy="payees-error" appearance={Appearance.DANGER} message={formik.errors?.payees} />
                </MessageContainer>
              )}
              {showOverdraftWarning && (
                <MessageContainer>
                  <Message dataCy="overdraft-warning" appearance={Appearance.WARNING} key="overdraftwarning" message={overdraftText} />
                </MessageContainer>
              )}
              {apiSubmitErrors?.errorMessages?.map((error, index) => (
                <Message key={`error-message-${index}`} appearance={Appearance.DANGER} message={error} />
              ))}
              {!hasSubmitPaymentPermissions && (
                <MessageContainer>
                  <Message dataCy="payment-submit-permissions" key="payment_submit_permissions" message="You do not have permissions to submit payments. Please contact Kickfin support." appearance={Appearance.WARNING} />
                </MessageContainer>
              )}
            </SidebarContentLayout>
          </PRSContent>

          <SidebarTermsFooter>
            <Checkbox
              onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                await setFieldValue('authorizedAgreement', e.target.checked);
                setFieldTouched('authorizedAgreement');
              }}
              id="authorizedAgreement"
              name="authorizedAgreement"
              data-cy="authorized-agreement-checkbox"
              checked={acceptAgreementProps.value}
              label={
                <span>
                  I am authorized to make these transactions and aware of the{' '}
                  <a href={appConfig.LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer">
                    Terms and Conditions
                  </a>{' '}
                  to send payments.
                </span>
              }
            />
            <SidebarActions>
              <Button label="Back" appearance="white" size="large" disabled={false} onClick={onBack} />
              <Button label="Submit" appearance="primary" size="large" data-cy="payment-review-submit-btn" disabled={submitButtonDisabled} isProcessing={isProcessing} onClick={onSubmit} />
            </SidebarActions>
          </SidebarTermsFooter>
        </>
      }
      sidebarOpen={sidebarOpen}
    />
  );
};

export default PaymentReview;
